/* Mobile Landing — Salik-style: integrated header, gradient bg, mint cards. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Books,
  BookBookmark,
  Buildings,
  CaretDown,
  Files,
  Gavel,
  MagnifyingGlass,
  Scales,
  ShieldCheck,
} from "@phosphor-icons/react";
import RingTiles from "./RingTiles";
import WeatherCard from "./WeatherCard";
import IntegratedHeader from "./IntegratedHeader";
import { useUser } from "../../context/UserAuthContext";
import { useAdmin } from "../../context/AdminAuthContext";
import { cassationCount } from "../../lib/api";
import { prefetchCases } from "../../lib/casesStore";
import { prefetchLaws, useLaws } from "../../lib/lawsStore";
import { prefetchRegulations } from "../../lib/regulationsStore";
import { prefetchConversations } from "../../lib/conversationsStore";

const CARD =
  "bg-white/55 backdrop-blur-md rounded-[28px] border border-white/60 shadow-[0_2px_18px_rgba(10,25,47,0.08)]";

export default function MobileLanding() {
  // Match the body background AND the iOS status-bar (theme-color) to the
  // gradient color visible at the TOP of viewport, so the device dead-zone
  // (notch/status bar) blends perfectly with the page.
  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = "#E4E4E4";
    document.documentElement.style.backgroundColor = "#E4E4E4";
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "#E4E4E4");
    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.documentElement.style.backgroundColor = prevHtmlBg;
      if (meta && prevTheme !== null) meta.setAttribute("content", prevTheme);
    };
  }, []);

  return (
    <>
      {/* Solid status-bar cover — guarantees the dead-zone (notch / status bar)
          has the exact same color as the page top, regardless of theme-color
          support on the device's browser. */}
      <div
        className="lg:hidden fixed top-0 inset-x-0 pointer-events-none"
        style={{
          height: "calc(env(safe-area-inset-top, 0px) + 8px)",
          background: "#E4E4E4",
          zIndex: -5,
        }}
        aria-hidden="true"
      />
      {/* Full-bleed background — extends well beyond viewport to cover safe-areas
          (notch/home-indicator) on all sides; the top portion is held at a SOLID
          color matching theme-color so the dead-zone has zero visible seam. */}
      <div
        className="lg:hidden fixed pointer-events-none -z-10"
        style={{
          top: "-40vh",
          bottom: "-20vh",
          left: 0,
          right: 0,
          background:
            "linear-gradient(180deg,#E4E4E4 0%,#E4E4E4 calc(40vh + env(safe-area-inset-top, 0px) + 60px),#E0E0E0 55%,#D0D0D0 80%,#C7C7C7 100%)",
        }}
        aria-hidden="true"
      />
      <div
        className="lg:hidden min-h-[100dvh] px-4 pb-4 lex-cairo-scope"
        style={{
          // Just-below the dead-zone: pad by safe-area only, no extra
          // breathing room. The wordmark sits flush against the status bar.
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
        data-testid="mobile-landing"
      >
      <IntegratedHeader />
      <HeroCard />
      <WeatherCard />
      <RingTiles />
      <SectionTitle eyebrow="القدرات" title="ست واجهات قانونية" />
      <FeaturesGrid />
      <SectionTitle eyebrow="لماذا" title="ثلاث ركائز" />
      <Pillars />
      <LawsCoveredCard />
      <CassationCard />
      <ClosingCTA />
      <PrincipleCard />
      </div>
    </>
  );
}

/* ================================== Cards ================================== */

function HeroCard() {
  const { user } = useUser();
  const { admin } = useAdmin();

  // Polite Arabic salutation, aligned to the inline-end (left in RTL).
  let saluteRole;
  let displayName;
  if (!user) {
    saluteRole = "سعادة الزائر";
    displayName = "";
  } else if (admin) {
    saluteRole = "سعادة مدير النظام";
    displayName = user.name && user.name !== "مدير النظام" ? user.name : "";
  } else if (user.role === "client") {
    saluteRole = "سعادة الزائر";
    displayName = user.name || "";
  } else {
    saluteRole = "سعادة المحامي";
    displayName = user.name || "";
  }

  return (
    <section className={`${CARD} px-4 py-4 mb-3`} data-testid="hero-card">
      <div
        data-testid="hero-user-line"
        className="text-start"
        dir="rtl"
      >
        <div className="font-tajawal text-[18px] font-extrabold text-[#0A192F] leading-tight">
          <span className="text-[#2D3748] font-extrabold">{saluteRole}</span>
          {displayName && (
            <>
              <span className="text-[#2D3748] font-extrabold">: </span>
              <span data-testid="hero-user-name">{displayName}</span>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/* AlertCard removed — replaced by WeatherCard on the home page. */

/* ============================== Section heading ============================ */

function SectionTitle({ eyebrow, title }) {
  return (
    <div className="px-2 pt-4 pb-2 text-start">
      <h2 className="font-tajawal leading-tight">
        <span className="text-[18px] font-extrabold text-[#0A192F]">{eyebrow}:</span>{" "}
        <span className="text-[18px] font-bold text-[#3A4654]">{title}</span>
      </h2>
    </div>
  );
}

/* ============================== Features grid ============================== */

const FEATURES = [
  { icon: MagnifyingGlass, title: "تحليل القضية", desc: "تشخيص، نقاط قوة/ضعف، احتمال نجاح.", href: "/cases/new", testid: "feature-analyze" },
  { icon: Files, title: "صياغة الوثائق", desc: "شكاوى، عرائض، طعون، وعقود.", href: "/draft", testid: "feature-draft" },
  // The "البحث القانوني" tile here used to duplicate the gold central AI
  // button in BottomNav + the "الأسئلة" RingTile, so on the same home
  // screen the user saw THREE entry points to the chat. We repurposed
  // this slot for the new "الأنظمة الداخلية" library — same visual
  // weight, but a distinct destination (a separate library with its own
  // collection, see `internal_regulations` collection + the dedicated
  // explorer at /internal-regulations).
  { icon: BookBookmark, title: "الأنظمة الداخلية", desc: "النظام الداخلي للمؤسسات والنقابات.", href: "/internal-regulations", testid: "feature-regulations" },
  { icon: Scales, title: "إدارة القضايا", desc: "أرشيف وتحليلات ومواعيد.", href: "/cases", testid: "feature-cases" },
  { icon: Books, title: "مكتبة القوانين", desc: "العقوبات، المدني، الأحوال الشخصية…", href: "/laws", testid: "feature-laws" },
  { icon: Buildings, title: "المحاكم", desc: "الاتحادية، التمييز، الاستئناف…", href: "/courts", testid: "feature-courts" },
];

// Route → prefetch handler. onPointerDown on the tile triggers the fetch
// ~150 ms before the actual click, so the destination often mounts with
// data already in memory.
const PREFETCH_BY_ROUTE = {
  "/cases": prefetchCases,
  "/laws": prefetchLaws,
  "/internal-regulations": prefetchRegulations,
  "/messages": prefetchConversations,
};

function FeaturesGrid() {
  return (
    <section className="grid grid-cols-2 gap-2.5 mb-3">
      {FEATURES.map((f) => (
        <Link
          key={f.href}
          to={f.href}
          data-testid={f.testid}
          // P4 — prefetch destination data at pointer-down so the target
          // list is often already in memory by the time React mounts it.
          onPointerDown={PREFETCH_BY_ROUTE[f.href]}
          className={`${CARD} p-4 active:scale-95 transition-transform`}
        >
          {/* Icon + title on the same line for a tighter, more scannable
              card layout. The description sits below on its own row. */}
          <div className="flex items-center gap-2 mb-1">
            <f.icon size={22} weight="duotone" className="text-[#D4AF37] shrink-0" />
            <h4 className="font-tajawal text-[17px] font-bold text-[#0A192F] leading-tight whitespace-nowrap">
              {f.title}
            </h4>
          </div>
          <p className="text-[13px] text-[#5A6470] leading-snug">{f.desc}</p>
        </Link>
      ))}
    </section>
  );
}

/* ================================ Pillars ================================== */

const PILLARS = [
  { n: "01", title: "محامٍ بخبرة 20 عاماً", desc: "يفكّر كمحامٍ عراقي كبير، بمنهج صياغة ودفاع رصينة." },
  { n: "02", title: "اختصاص عراقي حصرياً", desc: "قانون عراقي، محاكم عراقية، تمييز عراقي. بدون خلط بلوائح أجنبية." },
  { n: "03", title: "خصوصية القضية", desc: "تحليلاتك ووثائقك معزولة في حسابك الخاص." },
];

function Pillars() {
  return (
    <section className="space-y-2.5 mb-3">
      {PILLARS.map((p) => (
        <article key={p.n} className={`${CARD} p-4 flex items-start gap-3`}>
          <div className="font-tajawal text-[37px] font-bold text-[#D4AF37] leading-none w-10 shrink-0">{p.n}</div>
          <div className="text-start flex-1">
            <h5 className="font-tajawal text-[19px] font-bold text-[#0A192F] mb-1">{p.title}</h5>
            <p className="text-[15px] text-[#5A6470] leading-relaxed">{p.desc}</p>
          </div>
        </article>
      ))}
    </section>
  );
}

/* ============================ Laws covered card =========================== */

function LawsCoveredCard() {
  // Reuses the shared useLaws() store — no duplicate fetch even if the
  // user has visited /laws recently. When the catalog is still cached
  // from a previous visit, the count is instant with no network hop.
  const { laws } = useLaws();
  const count = Array.isArray(laws) ? laws.length : null;
  const display = count == null || count === 0 ? "…" : String(count);
  // Shrink the giant number when it exceeds 4 digits so it stays on one line.
  const sizeClass = count != null && count >= 10000 ? "text-[34px]" : "text-[46px]";
  return (
    <section className={`${CARD} p-5 mb-3`} data-testid="laws-covered-card">
      <div className="flex items-start justify-between mb-4">
        <span className="bg-white rounded-full px-3 py-1 text-[14px] text-[#0A192F]">إحصائية مرجعية</span>
        <h3 className="text-[18px] font-bold text-[#0A192F]">القوانين المدعومة</h3>
      </div>
      <div className="flex items-center gap-3">
        <Books size={26} weight="duotone" className="text-[#0A192F]" />
        <div className="flex-1 text-start">
          <div className="text-[17px] text-[#0A192F]">قانون عراقي مدعوم</div>
          <div className="text-[14px] text-[#5A6470] mt-0.5">المدني، الجزائي، التجاري، العمل، الأحوال…</div>
        </div>
        <div
          className={`font-tajawal ${sizeClass} font-bold text-[#0A192F] leading-none`}
          data-testid="laws-count"
        >
          {display}<span className="text-[28px] ms-0.5">+</span>
        </div>
        <div className="text-[14px] text-[#5A6470]">قانون</div>
      </div>
      <Link
        to="/laws"
        data-testid="laws-card-link"
        className="mt-4 pt-3 border-t border-white/70 flex items-center justify-between text-[17px] text-[#0A192F]"
      >
        <CaretDown size={14} weight="bold" />
        <span>تصفّح القوانين</span>
      </Link>
    </section>
  );
}

/* =============================== Cassation ================================= */

function CassationCard() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      cassationCount()
        .then((n) => { if (!cancelled) setCount(n); })
        .catch(() => { if (!cancelled && count == null) setCount(0); });
    };
    fetchCount();
    // Re-poll every 15s so the home card reflects the growing library,
    // but pause when the tab is hidden — the cassation count is purely
    // cosmetic on the home card; nobody needs it to tick up while the
    // app is backgrounded (saves ~240 requests/hour per idle user).
    let tid = null;
    const start = () => { if (tid == null) tid = setInterval(fetchCount, 15000); };
    const stop = () => { if (tid != null) { clearInterval(tid); tid = null; } };
    const onVis = () => {
      if (document.hidden) stop();
      else { fetchCount(); start(); }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const display = count == null ? "…" : count.toLocaleString("en-US");
  const sizeClass = count != null && count >= 10000 ? "text-[34px]" : "text-[46px]";
  return (
    <section className={`${CARD} p-5 mb-3`} data-testid="cassation-card">
      <div className="flex items-start justify-between mb-4">
        <span className="bg-white rounded-full px-3 py-1 text-[14px] text-[#0A192F]">مكتبة مرجعية</span>
        <h3 className="text-[18px] font-bold text-[#0A192F]">قرارات التمييز</h3>
      </div>
      <div className="flex items-center gap-3">
        <Gavel size={26} weight="duotone" className="text-[#0A192F]" />
        <div className="flex-1 text-start">
          <div className="text-[17px] text-[#0A192F]">قرارات قابلة للبحث</div>
          <div className="text-[14px] text-[#5A6470] mt-0.5">بحث بالنوع والكلمات والمبدأ</div>
        </div>
        <div
          className={`font-tajawal ${sizeClass} font-bold text-[#0A192F] leading-none`}
          data-testid="cassation-count"
        >
          {display}
        </div>
        <div className="text-[14px] text-[#5A6470]">قرار</div>
      </div>
      <Link
        to="/cassation"
        data-testid="cassation-card-link"
        className="mt-4 pt-3 border-t border-white/70 flex items-center justify-between text-[17px] text-[#0A192F]"
      >
        <CaretDown size={14} weight="bold" />
        <span>تصفّح المكتبة</span>
      </Link>
    </section>
  );
}

/* =============================== Closing CTA =============================== */
/* Copy-only card (no button) — the "افتح ورشة" action lives in the AI
   bottom-nav pill, so a duplicate button on the landing was redundant. */

function ClosingCTA() {
  return (
    <section className={`${CARD} p-5 mb-3 text-start`} data-testid="closing-cta">
      <div className="text-[14px] font-bold text-[#B8860B] mb-2">ابدأ الآن</div>
      <h3 className="font-tajawal text-[22px] font-bold text-[#0A192F] leading-tight mb-2">
        قدّم القضية، ودَع التحليل لمن يفهم القانون العراقي.
      </h3>
      <p className="text-[15px] text-[#5A6470] leading-relaxed">
        ادخل الوقائع، واحصل خلال ثوانٍ على المواد المنطبقة، القوة والضعف، احتمال النجاح،
        والإجراءات المطلوبة.
      </p>
    </section>
  );
}

/* =============================== Principle ================================ */

function PrincipleCard() {
  return (
    <section className={`${CARD} p-5`} data-testid="principle-card">
      <div className="flex items-center justify-end gap-2 mb-3">
        <ShieldCheck size={18} weight="duotone" className="text-[#D4AF37]" />
        <span className="text-[14px] font-bold text-[#5A6470]">مبدأ قانوني</span>
      </div>
      <p className="font-tajawal text-[26px] text-[#0A192F] leading-[1.7] text-center">
        «العَدلُ أَساسُ المُلك، والقَضاءُ حِصنُها الحَصين.»
      </p>
      <div className="mt-3 mx-auto w-10 h-[2px] bg-[#D4AF37]" />
    </section>
  );
}
